import { ref } from 'vue';

type ColorScheme = 'light' | 'dark';

const STORAGE_KEY = 'color-scheme';

// What the design system's dark token block keys off.
const ATTRIBUTE = 'data-mui-color-scheme';

function read(): ColorScheme | null {
  const value = document.documentElement.getAttribute(ATTRIBUTE);
  return value === 'light' || value === 'dark' ? value : null;
}

function apply(value: ColorScheme): void {
  document.documentElement.setAttribute(ATTRIBUTE, value);
}

// Embedded, a future host shell would already have written the attribute and own the choice;
// standalone falls through to our own storage and the OS preference.
function initialScheme(): ColorScheme {
  const hosted = read();
  if (hosted) return hosted;

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

const scheme = ref<ColorScheme>(initialScheme());
apply(scheme.value);

// A future host shell could flip the attribute directly; follow it so the design-system tokens
// and Vuetify's theme never disagree.
new MutationObserver(() => {
  const next = read();
  if (next) scheme.value = next;
}).observe(document.documentElement, { attributeFilter: [ATTRIBUTE] });

/** Standalone color-scheme toggle. */
export function useColorScheme() {
  function toggle(): void {
    scheme.value = scheme.value === 'dark' ? 'light' : 'dark';
    localStorage.setItem(STORAGE_KEY, scheme.value);
    apply(scheme.value);
  }
  return { scheme, toggle };
}
